import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Search, Edit, Trash2, Eye, Filter, RefreshCw,
    Target, Calendar, ShieldCheck, CheckCircle2, XCircle
} from "lucide-react";

import {
    deleteCampagneRequest,
    fetchCampagnesRequest
} from "../../redux/actions/campagnesActions";
import CampagnePerformanceForm from "./CampagnePerformanceForm";
import "./PerformanceStyles.css";

const CampagnePerformanceManagement = () => {
    const dispatch = useDispatch();
    const campagnesState = useSelector(s => s.campagnes || {});
    const campagnes = campagnesState.list || [];
    const loading = campagnesState.loading || [];


    const [searchTerm, setSearchTerm] = useState("");
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedCampagneId, setSelectedCampagneId] = useState(null);
    const [filterStatus, setFilterStatus] = useState("ALL"); // ALL, ACTIVE, INACTIVE

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    // Filtrer pour n'afficher que les campagnes PERFORMANCE
    const performanceCampagnes = useMemo(() => {
        return (campagnes || []).filter(c => c.typeCampagne === "PERFORMANCE");
    }, [campagnes]);

    const filteredList = useMemo(() => {
        return performanceCampagnes.filter(c => {
            const matchesSearch = c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                 c.reference?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = filterStatus === "ALL" || 
                                 (filterStatus === "ACTIVE" && c.status) || 
                                 (filterStatus === "INACTIVE" && !c.status);
            return matchesSearch && matchesStatus;
        });
    }, [performanceCampagnes, searchTerm, filterStatus]);

    const handleAdd = () => {
        setSelectedCampagneId(null);
        setIsFormOpen(true);
    };

    const handleEdit = (id) => {
        setSelectedCampagneId(id);
        setIsFormOpen(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Supprimer cette campagne ?",
            text: "Cette action est irréversible.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler"
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCampagneRequest(id));
            }
        });
    };

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("fr-FR");
    };

    return (
        <div className="perf-page">
            <div className="perf-header">
                <div>
                    <h1>Gestion des Campagnes de Performance</h1>
                    <p>Pilotez les objectifs et les campagnes d'évaluation annuelle.</p>
                </div>
                <button className="perf-btn perf-btn--primary" onClick={handleAdd}>
                    <Plus size={16} />
                    Nouvelle campagne
                </button>
            </div>

            <div className="perf-toolbar">
                <div className="perf-search-wrap">
                    <Search size={16} className="perf-search-icon" />
                    <input 
                        type="text" 
                        className="perf-search" 
                        placeholder="Rechercher par nom ou référence..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="cr-filter-tabs">
                    <button 
                        className={`cr-filter-tab ${filterStatus === "ALL" ? "active" : ""}`}
                        onClick={() => setFilterStatus("ALL")}
                    >
                        Toutes ({performanceCampagnes.length})
                    </button>
                    <button 
                        className={`cr-filter-tab ${filterStatus === "ACTIVE" ? "active" : ""}`}
                        onClick={() => setFilterStatus("ACTIVE")}
                    >
                        Actives ({performanceCampagnes.filter(c => c.status).length})
                    </button>
                    <button 
                        className={`cr-filter-tab ${filterStatus === "INACTIVE" ? "active" : ""}`}
                        onClick={() => setFilterStatus("INACTIVE")}
                    >
                        Inactives ({performanceCampagnes.filter(c => !c.status).length})
                    </button>
                </div>

               
            </div>

            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                        <tr>
                            <th>Campagne</th>
                            <th>Référence</th>
                            <th>Période</th>
                            <th>Objectifs (Max/Qant/Qual)</th>
                            <th>Validation</th>
                            <th>Statut</th>
                            <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="cr-empty-cell">
                                    {loading ? "Chargement des données..." : "Aucune campagne de performance trouvée."}
                                </td>
                            </tr>
                        ) : (
                            filteredList.map(c => (
                                <tr key={c.id}>
                                    <td>
                                        <div className="cr-strong">{c.nom}</div>
                                        <div className="cr-muted">Année {c.anneeCampagne}</div>
                                    </td>
                                    <td><code className="cr-muted">{c.reference}</code></td>
                                    <td>
                                        <div className="cr-muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <Calendar size={12} />
                                            {formatDate(c.dateDebut)} — {formatDate(c.dateFin)}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", gap: 4 }}>
                                            <span className="perf-badge perf-badge--blue" title="Total Max">{c.nombreMaxObjectifs}</span>
                                            <span className="perf-badge perf-badge--blue" title="Quantitatifs">{c.nombreObjectifsQuantitatifs} Qt</span>
                                            <span className="perf-badge perf-badge--blue" title="Qualitatifs">{c.nombreObjectifsQualitatifs} Ql</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="perf-badge perf-badge--blue">
                                            <ShieldCheck size={12} />
                                            {c.validation}
                                        </span>
                                    </td>
                                    <td>
                                        {c.status ? (
                                            <span className="perf-badge perf-badge--green">
                                                <CheckCircle2 size={12} /> Active
                                            </span>
                                        ) : (
                                            <span className="perf-badge perf-badge--red">
                                                <XCircle size={12} /> Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        <div className="cr-row-actions">
                                            <button className="cr-icon-btn" onClick={() => handleEdit(c.id)} title="Modifier">
                                                <Edit size={16} />
                                            </button>
                                            <button className="cr-icon-btn cr-icon-btn--danger" onClick={() => handleDelete(c.id)} title="Supprimer">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {isFormOpen && (
                <CampagnePerformanceForm 
                    open={isFormOpen}
                    campagneId={selectedCampagneId}
                    campagnes={performanceCampagnes}
                    onClose={() => setIsFormOpen(false)}
                />
            )}
        </div>
    );
};

export default CampagnePerformanceManagement;
